import { OEText, flex, mb, height } from '@openeducation/oe-rn-components';
import { TestId } from '@openeducation/oe-rn-components/src/constants/test-id';
import { useLiveClassRubrics } from '@openeducation/oe-rn-providers';
import type {
  LiveClassCriteriaPerformanceDTO,
  LiveClassCriteriaDTO,
} from '@openeducation/oe-rn-services';
import { styled } from '@openeducation/oe-rn-themes';
import React, { useCallback, useState, useEffect } from 'react';
import { View } from 'react-native';

import { PCFeedbackRubricsLoader } from './pc-feedback-rubrics-loader';
import PCFeedbackRubricItem from '../../molecules/pc-feedback-rubric-item/pc-feedback-rubric-item';

export interface PCFeedbackRubricsProps {
  lessonId?: number;
  criteriaPerformanceIds: number[];
  onCriterionPress: (ids: number[]) => void;
}

type SelectedIds = {
  [criterionId: number]: number | null;
};

const PCFeedbackRubrics = ({
  lessonId,
  criteriaPerformanceIds,
  onCriterionPress,
}: PCFeedbackRubricsProps) => {
  const [selectedIds, setSelectedIds] = useState<SelectedIds>({});
  const [titleHeight, setTitleHeight] = useState<number>(0);

  const { data, isLoading, isError } = useLiveClassRubrics({
    lessonId,
  });

  useEffect(() => {
    const criteriaDTOs = data?.liveClassCriteriaDTOs || [];
    if (criteriaDTOs.length > 0) {
      setSelectedIds(
        criteriaDTOs.reduce(
          (acc: SelectedIds, criterion: LiveClassCriteriaDTO) => ({
            ...acc,
            [criterion.id]: null,
          }),
          {}
        )
      );
    }
  }, [data]);

  useEffect(() => {
    const performanceIds = Object.values(selectedIds).filter(
      (id) => id !== null
    ) as number[];

    if (performanceIds.length > 0) {
      onCriterionPress(performanceIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds]);

  const onItemPress = useCallback((criterionId: number, itemId: number) => {
    setSelectedIds((prev: SelectedIds) => ({ ...prev, [criterionId]: itemId }));
  }, []);

  const renderRubrics = useCallback(() => {
    if (!data) {
      return null;
    }

    const numOfRubrics = data.liveClassCriteriaDTOs.length;

    return data.liveClassCriteriaDTOs.map(
      (criterion: LiveClassCriteriaDTO, index: number) => {
        const isLast = numOfRubrics - 1 === index;

        if (!criterion) {
          return null;
        }

        return (
          <Column
            isLast={isLast}
            key={criterion.name}
            testID="pc-feedback-rubric"
          >
            <View style={[mb(24), height(titleHeight)]}>
              <OEText
                fontSize={16}
                onLayout={(e) => {
                  const thisTitleHeight = e?.nativeEvent?.layout?.height;
                  setTitleHeight((prev: number) =>
                    thisTitleHeight > prev ? thisTitleHeight : prev
                  );
                }}
              >
                {criterion.name}
              </OEText>
            </View>
            {(criterion?.liveClassCriteriaPerformanceDTOs || []).map(
              ({ name, id }: LiveClassCriteriaPerformanceDTO) => (
                <PCFeedbackRubricItem
                  key={id}
                  isChecked={criteriaPerformanceIds.includes(id)}
                  onPress={() => onItemPress(criterion.id, id)}
                >
                  {name}
                </PCFeedbackRubricItem>
              )
            )}
          </Column>
        );
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, criteriaPerformanceIds, titleHeight]);

  if (
    !lessonId ||
    isError ||
    (data?.learningObjectives.length === 0 && !isLoading)
  ) {
    return null;
  }

  if (isLoading) {
    return <PCFeedbackRubricsLoader />;
  }

  return (
    <View style={[flex('flex-row')]} testID={TestId.PC_FEEDBACK_RUBRICS}>
      {renderRubrics()}
    </View>
  );
};

type ColumnType = {
  isLast?: boolean;
};

const Column = styled.View<ColumnType>`
  flex: 1;
  border-right-width: ${({ isLast }) => (isLast ? 0 : 1)}px;
  border-right-color: ${({ theme }) => theme.color.border};
  padding-right: ${({ isLast }) => (isLast ? 0 : 16)}px;
  margin-right: ${({ isLast }) => (isLast ? 0 : 16)}px;
  padding-top: 16px;
`;

export { PCFeedbackRubrics };
